/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState } from "react";
import { RatingEscalationSettings } from "../components/RatingEscalationSettings";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { toast } from "../components/ui/sonner";
import { Workflow, Play, Zap, Loader2, CheckCircle2, Archive, Hash, MessageSquare, Save, Info } from "lucide-react";

export default function CRMAutomations() {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const canBackfill = ["admin", "ceo", "eng_manager"].includes(user?.baseRole || user?.role);

  // Missed-call auto SMS settings (admin-configurable)
  const [sms, setSms] = useState(null);
  const [smsSaving, setSmsSaving] = useState(false);
  const loadSms = () => api.get("/settings/missed-sms").then(({ data }) => setSms(data)).catch(() => {});
  useEffect(() => { loadSms(); }, []); // eslint-disable-line
  const saveSms = async () => {
    setSmsSaving(true);
    try {
      const { data } = await api.put("/settings/missed-sms", { enabled: sms.enabled, template: sms.template });
      setSms((s) => ({ ...s, ...data }));
      toast.success(zh ? "未接短信设置已保存" : "Missed-call SMS settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Save failed")); }
    setSmsSaving(false);
  };
  const preview = sms ? (sms.template || "")
    .replaceAll("{clientName}", zh ? "王先生" : "Mr. Wang")
    .replaceAll("{clientNameEn}", zh ? " 王先生" : " Mr. Wang")
    .replaceAll("{companyPhone}", sms.companyPhone || "") : "";

  useEffect(() => { api.get("/crm/automations").then(({ data }) => setRules(data || [])).catch(() => {}); }, []); // eslint-disable-line


  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/crm/automations/run"); toast.success(zh ? `已执行：跟进提醒 ${data.remindersSent} 条，预热 ${data.nurtureSent} 条` : `Sent ${data.remindersSent} reminders, ${data.nurtureSent} nurtures`); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "执行失败" : "Failed")); }
    setBusy(false);
  };

  const backfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/crm/leads/backfill-signed-archive");
      toast.success(zh ? `补档完成：${data.processed} 个项目已补入报价/合同，${data.alreadyArchived} 个已归档跳过` : `Backfilled ${data.processed} project(s), skipped ${data.alreadyArchived} already archived`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "补档失败" : "Backfill failed")); }
    setBackfilling(false);
  };

  const [backfillingNo, setBackfillingNo] = useState(false);
  const backfillContractNo = async () => {
    setBackfillingNo(true);
    try {
      const { data } = await api.post("/crm/leads/backfill-contract-no");
      toast.success(zh ? `合同编号回填完成：为 ${data.assigned} 份历史合同补上正式编号` : `Assigned contract numbers to ${data.assigned} historical contracts`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "回填失败" : "Backfill failed")); }
    setBackfillingNo(false);
  };

  return (
    <div className="space-y-4" data-testid="crm-automations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Workflow className="h-6 w-6 text-violet-600" />{zh ? "自动化工作流" : "Automations"}</h1>
          <p className="text-sm text-slate-400 mt-0.5">{zh ? "跟进提醒/升级、培育预热等自动规则（邮件/短信为 Mock）" : "Follow-up & nurture automations (email/SMS mocked)"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canBackfill && (
            <Button data-testid="crm-backfill-archive" onClick={backfill} disabled={backfilling} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">{backfilling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Archive className="h-4 w-4 mr-1" />}{zh ? "历史签约补档" : "Backfill signed archives"}</Button>
          )}
          {canBackfill && (
            <Button data-testid="crm-backfill-contract-no" onClick={backfillContractNo} disabled={backfillingNo} variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50">{backfillingNo ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Hash className="h-4 w-4 mr-1" />}{zh ? "回填合同编号" : "Backfill contract No."}</Button>
          )}
          <Button data-testid="crm-run-automations" onClick={run} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}{zh ? "立即执行一次" : "Run now"}</Button>
        </div>
      </div>

      {canBackfill && (
        <div data-testid="crm-backfill-hint" className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          {zh ? "历史签约补档：为早期已签约但缺少归档的项目，一键把报价单 + 施工合同 PDF 补入其「项目档案 · 签约归档」，供施工/财务调阅（幂等，已归档会自动跳过）。" : "Backfill: one-click archive of estimate + contract PDFs into the project files for earlier signed projects that were missing them (idempotent)."}
        </div>
      )}

      {/* Missed-call auto SMS settings (admin-configurable) */}
      {sms && (
        <div data-testid="missed-sms-settings" className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <MessageSquare className="h-5 w-5 text-sky-600" />
            <h2 className="text-base font-bold text-slate-900">{zh ? "未接来电自动短信" : "Missed-call auto SMS"}</h2>
            {!sms.smsConfigured && <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">{zh ? "短信服务未配置" : "SMS not configured"}</span>}
            <label className="ml-auto inline-flex items-center gap-2 cursor-pointer select-none">
              <span className="text-sm text-slate-600">{zh ? (sms.enabled ? "已开启" : "已关闭") : (sms.enabled ? "On" : "Off")}</span>
              <button
                type="button"
                role="switch"
                aria-checked={sms.enabled}
                data-testid="missed-sms-toggle"
                disabled={!sms.canEdit}
                onClick={() => sms.canEdit && setSms((s) => ({ ...s, enabled: !s.enabled }))}
                className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${sms.enabled ? "bg-emerald-500" : "bg-slate-300"} ${!sms.canEdit ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`)}>
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${sms.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </label>
          </div>
          <p className="text-xs text-slate-400">{zh ? "客户来电未接或留言后，系统自动给客户发送一条双语跟进短信。可在下方自定义内容与开关。" : "Auto-send a follow-up SMS to callers after a missed/voicemail call."}</p>

          <div>
            <label className="text-xs font-medium text-slate-500">{zh ? "短信模板" : "Template"}</label>
            <textarea
              data-testid="missed-sms-template"
              value={sms.template}
              disabled={!sms.canEdit}
              onChange={(e) => setSms((s) => ({ ...s, template: e.target.value }))}
              rows={4}
              className="mt-1 w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:text-slate-400"
            />
            <div className="flex-wrap md:flex-nowrap mt-1.5 flex items-start gap-1.5 text-[11px] text-slate-400">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{zh ? "可用占位符：" : "Placeholders: "}
                <code className="bg-slate-100 rounded px-1 mx-0.5 text-slate-600">{"{clientName}"}</code>{zh ? "客户名(中文位)" : "name"}
                <code className="bg-slate-100 rounded px-1 mx-0.5 text-slate-600">{"{clientNameEn}"}</code>{zh ? "客户名(英文位·自动带空格)" : "name (EN)"}
                <code className="bg-slate-100 rounded px-1 mx-0.5 text-slate-600">{"{companyPhone}"}</code>{zh ? `公司电话(${sms.companyPhone || "-"})` : "company phone"}
              </span>
            </div>
          </div>

          <div className="rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid="missed-sms-preview">
            <p className="text-[11px] font-medium text-slate-400 mb-1">{zh ? "预览（示例客户名）" : "Preview"}</p>
            <p className="text-sm text-slate-700 whitespace-pre-line">{preview}</p>
          </div>

          {sms.canEdit ? (
            <div className="flex justify-end">
              <Button data-testid="missed-sms-save" onClick={saveSms} disabled={smsSaving} className="bg-sky-600 hover:bg-sky-700 text-white">
                {smsSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}{zh ? "保存设置" : "Save"}
              </Button>
            </div>
          ) : (
            <p className="text-[11px] text-slate-400 text-right">{zh ? "仅管理员可修改此设置" : "Admin only"}</p>
          )}
        </div>
      )}

      {["admin", "ceo", "sales_vp"].includes(user?.role) && <RatingEscalationSettings api={api} zh={zh} />}

      <div className="grid md:grid-cols-2 gap-3">
        {rules.map((r) => (
          <div key={r.id} data-testid={`automation-${r.id}`} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex-wrap md:flex-nowrap flex items-start justify-between">
              <div className="flex-wrap md:flex-nowrap flex items-center gap-2">
                <Zap className="h-4 w-4 text-violet-500" />
                <span className="font-semibold text-slate-800">{r.name}</span>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${r.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"}`}>
                {r.enabled && <CheckCircle2 className="h-3 w-3" />}{r.enabled ? (zh ? "已启用" : "On") : (zh ? "关闭" : "Off")}
              </span>
            </div>
            <div className="mt-2 text-xs text-slate-500 space-y-1">
              <p><span className="text-slate-400">{zh ? "触发" : "When"}: </span>{r.trigger}</p>
              <p><span className="text-slate-400">{zh ? "动作" : "Then"}: </span>{r.action}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
